/* eslint-disable max-len */
import { Button, Stack } from '@mui/material';
import CifaAPI from 'api/cifa/cifa.api';
import AlertModal from 'components/shared/alert_modal';
import DataConfirmation from 'components/shared/data_confirmation';
import { PageTitle } from 'components/shared/page_title';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import useCifaStore from 'stores/cifa/cifa.store';

interface Step2Props {
  onBack: () => void;
  formik: any;
}

const Step2: React.FC<Step2Props> = ({ onBack, formik }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { cifa } = useCifaStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);

  const anoReferencia = match ? Number(match[1]) : 2026;

  const formatarValorParaExibicao = (valor: number | string | null | undefined) => {
    if (!valor && valor !== 0) return 'R$ 0,00';

    const numeroValor = typeof valor === 'string' ? parseFloat(valor) : valor;

    if (isNaN(numeroValor)) return 'R$ 0,00';

    return `R$ ${numeroValor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleSave = async () => {
    const fieldsToUpdate = {
      alimentacao_af_recurso_pnae: formik.values.alimentacao_af_recurso_pnae,
      alimentacao_af_recurso_estado: formik.values.alimentacao_af_recurso_estado,
      alimentacao_af_recurso_prefeitura: formik.values.alimentacao_af_recurso_prefeitura,
      alimentacao_total_recurso_pnae: formik.values.alimentacao_total_recurso_pnae,
      alimentacao_total_recurso_estado: formik.values.alimentacao_total_recurso_estado,
      alimentacao_total_recurso_prefeitura: formik.values.alimentacao_total_recurso_prefeitura,
      alimentacao_ata_aprovacao_uuid: formik.values.alimentacao_ata_aprovacao_uuid,
    }
    const response = await CifaAPI.updateSchoolFeeding(cifa.uuid as string, fieldsToUpdate);
    console.log(response);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string);
  };

  return (
    <>
      <PageTitle
        title='Confirmação dos Dados de Alimentação Escolar'
        subtitle='Confira todas as informações sobre os valores de alimentação escolar que foram preenchidas, e altere-as caso necessário.'
      />

      <Stack marginX='4rem' spacing='2rem' maxWidth='1152px'>
        <DataConfirmation
          title='Valores Aplicados nas Aquisições da Agricultura Familiar'
          data={[
            {
              label: 'Recurso do PNAE Federal',
              value: formatarValorParaExibicao(formik.values.alimentacao_af_recurso_pnae),
            },
            {
              label: 'Recurso do Governo do Estado',
              value: formatarValorParaExibicao(formik.values.alimentacao_af_recurso_estado),
            },
            {
              label: 'Recurso da Prefeitura',
              value: formatarValorParaExibicao(formik.values.alimentacao_af_recurso_prefeitura),
            },
            {
              label: 'Total de Recursos da Agricultura Familiar',
              value: formatarValorParaExibicao(
                Number(formik.values.alimentacao_af_recurso_pnae || 0) +
                Number(formik.values.alimentacao_af_recurso_estado || 0) +
                Number(formik.values.alimentacao_af_recurso_prefeitura || 0)
              ),
            },
          ]}
        />

        <DataConfirmation
          title='Valores Totais Aplicados na Alimentação Escolar'
          data={[
            {
              label: 'Total de Recursos PNAE Federal',
              value: formatarValorParaExibicao(formik.values.alimentacao_total_recurso_pnae),
            },
            {
              label: 'Total de Recursos do Governo do Estado',
              value: formatarValorParaExibicao(formik.values.alimentacao_total_recurso_estado),
            },
            {
              label: 'Total de Recursos da Prefeitura',
              value: formatarValorParaExibicao(formik.values.alimentacao_total_recurso_prefeitura),
            },
            {
              label: 'Total Geral de Recursos na Alimentação Escolar',
              value: formatarValorParaExibicao(
                Number(formik.values.alimentacao_total_recurso_pnae || 0) +
                Number(formik.values.alimentacao_total_recurso_estado || 0) +
                Number(formik.values.alimentacao_total_recurso_prefeitura || 0)
              ),
            },
          ]}
        />

        <DataConfirmation
          title='Documentação de Aprovação'
          data={[
            {
              label: 'Ata de Aprovação dos Dados de Alimentação Escolar',
              value: formik.values.alimentacao_ata_aprovacao_uuid ? 'Anexada' : 'Não anexada',
            },
          ]}
          document_uuid={formik.values.alimentacao_ata_aprovacao_uuid}
          document_show={!!formik.values.alimentacao_ata_aprovacao_uuid}

        />

        <Stack
          direction='row'
          spacing={2}
          justifyContent='center'
          alignItems='center'
          marginTop='2rem'
        >
          <Button variant='text' color='primary' onClick={onBack}>
            Voltar
          </Button>
          <Button variant='contained' color='primary' onClick={handleSave}>
            Confirmar e Salvar
          </Button>
        </Stack>

        <AlertModal
          open={isModalOpen}
          title='Dados de Alimentação Escolar Salvos com Sucesso'
          message={`Os valores de alimentação escolar foram salvos com sucesso. Continue realizando o preenchimento das próximas etapas para concluir o cadastro do Município no CIFA ${anoReferencia}.`}
          buttonLabel='Avançar'
          onClose={handleCloseModal}
        />
      </Stack>
    </>
  );
};

export default Step2;
